import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery, useGetBrokerGroupUsersQuery } from '@/features/user/userApi';
import { UserAutocomplete } from '@/components/selects/UserAutocomplete';

// Thin wrapper around OwnerAutocomplete for callers that only have an ambiguous
// id (hubspotId or Firestore doc id) rather than the doc id OwnerAutocomplete
// needs for display. Resolves that id to a user first, then hands
// OwnerAutocomplete the doc id. `onChange` is passed straight through — the
// caller gets whatever OwnerAutocomplete emits (the full user object, or null).
export const OwnerSelect = ({
	initialId,
	disabled,
	onChange,
	minimal,
	allowRemoval = false,
	hideAvatar = false,
	showGroup = false,
	filterBy,
}) => {
	// getUserDetails' `userId` param resolves a doc id first and falls back to a
	// hubspotId, so this works regardless of which shape `initialId` is.
	const { data: initialOwner } = useGetUserDetailsQuery(
		{ userId: initialId },
		{ skip: !initialId },
	);

	// getBrokerGroupUsers entries key off id/docId (the Firestore doc id);
	// getUserDetails returns the same person keyed off userId — reshape so
	// OwnerAutocomplete can show them even if they're outside the fetched group.
	const fallbackUser = initialOwner
		? { ...initialOwner, id: initialOwner.userId, docId: initialOwner.userId }
		: null;

	return (
		<OwnerAutocomplete
			userId={initialOwner?.userId}
			disabled={disabled}
			minimal={minimal}
			showGroup={showGroup}
			hideAvatar={hideAvatar}
			disableClearable={!allowRemoval}
			onChange={onChange}
			fallbackUser={fallbackUser}
			{...(filterBy && { filterBy })}
		/>
	);
};

OwnerSelect.displayName = 'OwnerSelect';

/**
 * Select for users in the current user's broker group (or their whole group network when
 * showGroup is on). Fetches the users itself and renders UserAutocomplete.
 *
 * Single mode (default): `userId` is a user doc id; onChange gets the chosen user object,
 * or null. `fallbackUser` is the currently-assigned owner, shown even if they aren't in the
 * fetched group (e.g. they've left it) — still selectable-away-from, just not re-selectable.
 *
 * `multiple`: `userId` is an array of doc ids, or of user objects when returnValue is
 * 'object'; onChange gets the same. Adds checkboxes, "All" and "Clear Filter";
 * isUserFixed users can't be removed.
 *
 * Users an admin has deactivated (isActive: false) aren't offered. One that's already chosen
 * (e.g. a record's owner) still shows, but can't be chosen again. Table filters pass
 * includeInactive: deactivated users are then listed with an "Inactive" chip and can be
 * chosen, so records they owned can still be found.
 */
export const OwnerAutocomplete = ({
	userId,
	onChange,
	multiple = false,
	returnValue = 'id',
	showGroup = true,
	includeInactive = false,
	filterBy = () => true, // Users to be filtered out
	isUserDisabled = () => false, // Users that cannot be selected
	isUserFixed = () => false, // Users that cannot be removed (multiple only)
	fallbackUser,
	disabled,
	...props
}) => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails, ...userQuery } = useGetUserDetailsQuery({ email }, { skip: !email });

	const brokerGroupUsersQuery = useGetBrokerGroupUsersQuery(
		{ userId: userDetails?.userId, showGroup },
		{ skip: !userDetails?.userId },
	);

	const users = useMemo(() => {
		// Users an admin has deactivated (isActive: false) aren't offered, except the ones
		// already chosen, so a saved owner or filter still shows who it is
		const selectedIds = (multiple ? (userId ?? []) : [userId]).map((selected) =>
			returnValue === 'object' ? selected?.id : selected,
		);
		const unique = (brokerGroupUsersQuery.data ?? []).filter(
			(user, index, all) =>
				all.findIndex((other) => other.id === user.id) === index &&
				filterBy(user) &&
				(includeInactive ||
					user.isActive !== false ||
					selectedIds.includes(user.id) ||
					selectedIds.includes(user.docId)),
		);
		if (fallbackUser?.id && !unique.some((user) => user.id === fallbackUser.id)) {
			return [...unique, fallbackUser];
		}
		return unique;
	}, [
		brokerGroupUsersQuery.data,
		filterBy,
		fallbackUser,
		userId,
		multiple,
		returnValue,
		includeInactive,
	]);

	// Callers may pass either id field; options are matched on `id`.
	const findUser = (value) => users.find((user) => user.id === value || user.docId === value);

	const value = multiple
		? (userId ?? [])
				.map((selected) => findUser(returnValue === 'object' ? selected?.id : selected)?.id)
				.filter(Boolean)
		: (findUser(userId)?.id ?? null);

	const handleChange = (next) => {
		if (!multiple) {
			onChange(findUser(next) ?? null);
			return;
		}
		onChange(returnValue === 'object' ? next.map((id) => findUser(id)) : next);
	};

	const isLoading = !brokerGroupUsersQuery.isSuccess || !userQuery.isSuccess;

	return (
		<UserAutocomplete
			options={users}
			value={value}
			onChange={handleChange}
			multiple={multiple}
			disabled={disabled || isLoading}
			loading={isLoading}
			isOptionDisabled={(user) =>
				isUserDisabled(user) ||
				user.id === fallbackUser?.id ||
				(!includeInactive && user.isActive === false)
			}
			markInactive={includeInactive}
			isOptionFixed={isUserFixed}
			{...(multiple && { showCheckbox: true, showAllOption: true, showClearOption: true })}
			{...props}
		/>
	);
};

OwnerAutocomplete.displayName = 'OwnerAutocomplete';
